import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { FEATURE_FLAGS, type FeatureFlagKey } from './constants';

const FeatureFlagContext = createContext<Set<string>>(new Set());

interface FeatureFlagProviderProps {
  children: ReactNode;
  flags: string[];
}

/**
 * Provider that makes feature flags available to the app.
 * Wrap your app with this provider and pass the flags from the user session.
 */
export function FeatureFlagProvider({ children, flags }: FeatureFlagProviderProps) {
  const flagSet = useMemo(() => new Set(flags), [flags]);
  return <FeatureFlagContext.Provider value={flagSet}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Hook to check if a feature flag is enabled for the current user.
 *
 * @param key - The feature flag key from FEATURE_FLAGS
 * @returns true if the flag is enabled, false otherwise
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const flags = useContext(FeatureFlagContext);
  return flags.has(FEATURE_FLAGS[key]);
}

/**
 * Hook to check if a feature flag is enabled by string key.
 * Use this when the flag key is dynamic or not in FEATURE_FLAGS.
 *
 * @param flagKey - The feature flag key string
 * @returns true if the flag is enabled, false otherwise
 */
export function useFeatureFlagByKey(flagKey: string): boolean {
  const flags = useContext(FeatureFlagContext);
  return flags.has(flagKey);
}

/**
 * Hook to get all enabled feature flags.
 * Useful for debugging or logging.
 */
export function useAllFeatureFlags(): Set<string> {
  return useContext(FeatureFlagContext);
}
